import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ANALYSIS_LEVEL_TO_ATTRIBUTE_CODE, AnalysisEntry, AnalysisLevel } from 'pages/trading/model';
import { useContext, useEffect, useMemo } from 'react';
import { useAnalysisData, useMessage } from 'pages/trading/queryHooks';
import { TradingMessageDisplayTile } from 'pages/trading/TradingMessageDisplay';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@mui/material';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { ChildLevelOutliersDisplay } from 'pages/trading/outlier/ChildLevelOutliersDisplay';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { getAnalysisLevelFromAttributeCode } from 'pages/trading/util';
import { AttributeCode, useModelService } from 'services/model/model.service';
import { AnalysisEntryBandChart } from 'pages/trading/outlier/AnalysisEntryBandChart';
import { TranslatedAttributeLabel } from 'pages/trading/TranslatedAttributeLabel';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { InfoButton } from 'components/InfoButton';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DetailsPagesInfoContent } from './DetailsPagesInfoContent';
import { TbIcon } from 'components/Figma';

export function OutlierDetailPage() {
  const { attributeCode } = useParams<{ attributeCode: AttributeCode }>();
  const { messageId } = useParams<{ messageId: string }>();

  const { setDetailPageBreadcrumb } = useBreadcrumbStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { currencyCode } = useContext(TenantConfigContext);
  const { setContent } = useInfoDrawer();

  const { getAttribute, getMeasuresByMetrics } = useModelService();

  const [ordGmvVal] = getMeasuresByMetrics(['ordGmvVal'], 'OD');
  const intl = useIntl();

  const analysisLevel = attributeCode ? getAnalysisLevelFromAttributeCode(attributeCode) : undefined;

  const CHILD_DISPLAYS = (['MARKETPLACE', 'CATEGORY', 'COUNTRY', 'BRAND', 'PRODUCT'] as AnalysisLevel[]).filter(
    al => al !== analysisLevel
  );

  const {
    data: message,
    isPending: messageIsPending,
    isSuccess: messageIsSuccess,
    isError: messageIsError,
    error: messageError,
  } = useMessage(messageId);
  const attributeKey = message?.relationships.find(r => r.level === analysisLevel)?.levelKey ?? '';

  const {
    data: analysisDataRaw,
    isPending: analysisDataIsPending,
    isEnabled: analysisDataIsEnabled,
    isSuccess: analysisDataSuccess,
    isError: analysisDataIsError,
    error: analysisDataError,
  } = useAnalysisData<AnalysisEntry>(message?.analysisDataId ?? undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [messageId]);

  useEffect(() => {
    setContent({
      titleKey: 'trading.info.title',
      content: <DetailsPagesInfoContent />,
    });

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const analysisEntryWithFormattedDates = (entry: AnalysisEntry): AnalysisEntry => ({
    ...entry,
    timeSeries: [
      ...entry.timeSeries.map(timeSeriesEntry => ({
        ...timeSeriesEntry,
        dateValue: new Date(timeSeriesEntry.date),
        date: intl.formatDate(new Date(timeSeriesEntry.date)),
      })),
    ],
    children: Object.keys(entry.children).reduce(
      (acc, elem) => ({
        ...acc,
        [elem]: entry.children[elem].map(childEntry => analysisEntryWithFormattedDates(childEntry)),
      }),
      {}
    ),
  });

  const analysisData = useMemo(() => {
    if (analysisDataRaw) {
      return {
        ...analysisDataRaw,
        data: [...analysisDataRaw.data.map(entry => analysisEntryWithFormattedDates(entry))],
      };
    }
    return undefined;
  }, [analysisDataRaw, intl]);

  const selectedEntry = useMemo(() => {
    if (message && analysisData) {
      return (analysisData?.data ?? []).find(ad => ad.attributeValue === attributeKey);
    }
    return undefined;
  }, [analysisData, message]);

  useEffect(() => {
    if (selectedEntry && attributeCode) {
      const outlierEntry = selectedEntry.timeSeries[selectedEntry.timeSeries.length - 1];
      setDetailPageBreadcrumb(location.pathname, [
        { attribute: 'messageId', values: [messageId ?? ''] },
        { attribute: attributeCode, values: [attributeKey] },
        { attribute: 'dateRange', values: [{ from: outlierEntry.dateValue, to: outlierEntry.dateValue }] },
      ]);
    }
  }, [selectedEntry]);

  const renderRangeChart = useMemo(() => {
    if (selectedEntry) {
      return (
        <AnalysisEntryBandChart selectedEntry={selectedEntry} measure={ordGmvVal} testId="trading-detail-gmv-trend" />
      );
    }
    return [];
  }, [selectedEntry]);

  const getChildDisplays = () => {
    const displayableChildren = CHILD_DISPLAYS.map(cd => ({
      displayLevel: cd,
      relations: (message?.relationships ?? []).filter(rel => rel.level === cd),
    })).filter(level => level.relations.length > 0);

    if (!selectedEntry) {
      return [];
    }

    const lastParentEntry = selectedEntry.timeSeries[selectedEntry.timeSeries.length - 1];
    const parentDiff = lastParentEntry.value - lastParentEntry.expectedValue;

    return displayableChildren.map(dc => (
      <ChildLevelOutliersDisplay
        key={dc.displayLevel}
        displayLevel={dc.displayLevel}
        entries={selectedEntry.children[ANALYSIS_LEVEL_TO_ATTRIBUTE_CODE[dc.displayLevel][0]] ?? []}
        parentDiff={parentDiff}
        sentiment={message?.sentiment}
      />
    ));
  };

  if (!renderRangeChart) return null;

  return (
    <div className="standard-page-container">
      <PageContainer>
        <PageSectionTitle
          rightComponent={<InfoButton tooltipText="View page information" showText />}
          testId="trading-detail-title"
        >
          <TranslatedAttributeLabel attributeCode={attributeCode ?? ''} attributeValue={attributeKey} />
        </PageSectionTitle>

        {messageIsPending && (
          <Tile className="p-0 overflow-hidden">
            <div className="bg-snow-50 w-full h-full">
              <Box className="flex justify-center py-8">
                <MetricsLoadingSpinner />
              </Box>
            </div>
          </Tile>
        )}
        {messageIsError && <Tile errorDetails={getErrorMessage(messageError)} />}
        {messageIsSuccess && <TradingMessageDisplayTile message={message} testId="trading-detail-message" />}
        {analysisDataIsEnabled && analysisDataIsPending && (
          <Tile>
            <MetricsLoadingSpinner />
          </Tile>
        )}
        <Tile
          isError={analysisDataIsError}
          titleId="trading.gmv.bandchart.title"
          additionalTitle={<FormattedMessage id="trading.gmv.bandchart.title.overtime" />}
          className="pb-20"
          errorDetails={getErrorMessage(analysisDataError)}
          testId="trading-detail-analysis-error"
        >
          {analysisDataSuccess ? renderRangeChart : null}
        </Tile>
        {analysisDataSuccess &&
          selectedEntry &&
          (attributeCode === 'chnMarketplace' || attributeCode === 'prdCategory') && (
            <Tile
              testId="trading-detail-entity-link"
              role="button"
              tabIndex={0}
              className="py-3"
              onClick={() =>
                void navigate(`${TRADING_ROOT}/drilldown/${attributeCode}/${attributeKey}${location.search}`)
              }
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void navigate(`${TRADING_ROOT}/drilldown/${attributeCode}/${attributeKey}${location.search}`);
                }
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <p className="text-lg font-bold">{attributeCode && getAttribute(attributeCode).name}</p>
                  <p className="text-base">
                    <TranslatedAttributeLabel attributeCode={attributeCode} attributeValue={attributeKey} />
                  </p>
                </div>
                <div className="flex items-center">
                  {message?.sentiment === 'POSITIVE' && <TbIcon name="TrendUp" size="xl" className="text-laurel-500" />}
                  {message?.sentiment === 'NEGATIVE' && (
                    <TbIcon name="TrendDown" size="xl" className="text-coral-500" />
                  )}
                  <div className="flex flex-col gap-0.5 px-6">
                    <p className="text-xs">{ordGmvVal.metric.name}</p>
                    <p className="font-bold">
                      {intl.formatNumber(selectedEntry.timeSeries[selectedEntry.timeSeries.length - 1]?.value ?? 0, {
                        style: 'currency',
                        currency: currencyCode,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </Tile>
          )}
        {getChildDisplays()}
      </PageContainer>
    </div>
  );
}
